import * as React from 'react';
import { Col, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import type { ConnectedProps } from 'react-redux';
import { Field } from 'redux-form';

import type AccountViewModel from 'ab-viewModels/account.viewModel';
import type { DeliverableDataListViewModel } from 'ab-viewModels/deliverableData.viewModel';

import { DeliverableDataType } from '@acceligentllc/shared/enums/deliverableDataType';

import Dropdown from 'af-fields/Dropdown';
import Textarea from 'af-fields/Textarea';

import DropdownAccountItem from 'af-components/DropdownAccountItem';

import * as AccountActions from 'af-actions/accounts';
import * as DeliverableDataActions from 'af-actions/deliverableData';

const renderDeliverableManagerItem = (option: AccountViewModel) => option ? <DropdownAccountItem option={option} /> : <div />;

type Props = ConnectedProps<typeof connector>;

const DELIVERABLE_MANAGERS_FILTER_BY: (keyof AccountViewModel)[] = ['firstName', 'lastName', 'uniqueId'];
const DELIVERABLE_NOTES_MAX_LENGTH = 250;
const DELIVERABLE_NOTES_NUMBER_OF_LINES = 10;

const DeliverableSection: React.FC = (props: Props) => {
	const { loadDeliverableData, findAllAssignableForDeliverable } = props;

	const [deliverableManagersForCompany, setDeliverableManagersForCompany] = React.useState<AccountViewModel[]>([]);
	const initialDeliverableData = React.useMemo(() => ({
		[DeliverableDataType.DELIVERABLE_CODE_STANDARD]: [],
		[DeliverableDataType.DELIVERABLE_SOFTWARE]: [],
		[DeliverableDataType.DELIVERABLE_FILE_FORMAT]: [],
		[DeliverableDataType.DELIVERABLE_DELIVERY_METHOD]: [],
		[DeliverableDataType.DELIVERABLE_DELIVERY_TIMELINE]: [],
	}), []);

	const [
		deliverableData,
		setDeliverableData,
	] = React.useState<DeliverableDataListViewModel>(initialDeliverableData);

	const lazyLoadDeliverableManagers = React.useCallback(async (isLazyLoaded: boolean) => {
		if (!isLazyLoaded) {
			const deliverableManagers = await findAllAssignableForDeliverable();
			setDeliverableManagersForCompany(deliverableManagers ?? []);
		}
	}, [findAllAssignableForDeliverable]);

	const lazyLoadDeliverableData = React.useCallback(async (isLazyLoaded: boolean) => {
		if (!isLazyLoaded) {
			const data = await loadDeliverableData();
			setDeliverableData(data ?? initialDeliverableData);
		}
	}, [initialDeliverableData, loadDeliverableData]);

	return (
		<Row>
			<Col sm={8}>
				<Field
					component={Dropdown}
					id="deliverableSoftwareId"
					label="Deliverable Software"
					labelKey="name"
					name="deliverableSoftwareId"
					onLazyLoad={lazyLoadDeliverableData}
					options={deliverableData[DeliverableDataType.DELIVERABLE_SOFTWARE]}
					propName="deliverableSoftware"
					valueKey="id"
					withCaret={true}
				/>
				<Field
					component={Dropdown}
					id="deliverableCodeStandardId"
					label="Deliverable Code Standard"
					labelKey="name"
					name="deliverableCodeStandardId"
					onLazyLoad={lazyLoadDeliverableData}
					options={deliverableData[DeliverableDataType.DELIVERABLE_CODE_STANDARD]}
					propName="deliverableCodeStandard"
					valueKey="id"
					withCaret={true}
				/>
				<Field
					component={Dropdown}
					id="deliverableFileFormatId"
					label="Deliverable File Format"
					labelKey="name"
					name="deliverableFileFormatId"
					onLazyLoad={lazyLoadDeliverableData}
					options={deliverableData[DeliverableDataType.DELIVERABLE_FILE_FORMAT]}
					propName="deliverableFileFormat"
					valueKey="id"
					withCaret={true}
				/>
			</Col>
			<Col sm={8}>
				<Field
					component={Dropdown}
					id="deliverableDeliveryMethodId"
					label="Deliverable Delivery Method"
					labelKey="name"
					name="deliverableDeliveryMethodId"
					onLazyLoad={lazyLoadDeliverableData}
					options={deliverableData[DeliverableDataType.DELIVERABLE_DELIVERY_METHOD]}
					propName="deliverableDeliveryMethod"
					valueKey="id"
					withCaret={true}
				/>
				<Field
					component={Dropdown}
					id="deliverableDeliveryTimelineId"
					label="Deliverable Delivery Timeline"
					labelKey="name"
					name="deliverableDeliveryTimelineId"
					onLazyLoad={lazyLoadDeliverableData}
					options={deliverableData[DeliverableDataType.DELIVERABLE_DELIVERY_TIMELINE]}
					propName="deliverableDeliveryTimeline"
					valueKey="id"
					withCaret={true}
				/>
				<Field
					component={Dropdown}
					filterable={true}
					filterBy={DELIVERABLE_MANAGERS_FILTER_BY}
					id="deliverableAssigneeId"
					isArrayField={true}
					label="Deliverable QA/QC Assignee *"
					name="deliverableAssigneeId"
					onLazyLoad={lazyLoadDeliverableManagers}
					options={deliverableManagersForCompany}
					propName="deliverableAssignee"
					renderMenuItem={renderDeliverableManagerItem}
					valueKey="id"
					withCaret={true}
				/>
			</Col>
			<Col sm={8}>
				<Field
					component={Textarea}
					id="accqa__job__deliverable-notes"
					label="Deliverable Notes"
					maxCharacters={DELIVERABLE_NOTES_MAX_LENGTH}
					name="deliverableNotes"
					placeholder="Enter Notes"
					rows={DELIVERABLE_NOTES_NUMBER_OF_LINES}
					showMaxCharactersLabel={true}
				/>
			</Col>
		</Row>
	);
};

function mapDispatchToProps() {
	return {
		loadDeliverableData: DeliverableDataActions.findAllForCompany,
		findAllAssignableForDeliverable: AccountActions.findAllAssignableForDeliverable,
	};
}

const connector = connect(null, mapDispatchToProps());

export default connector(DeliverableSection);
